import type { WatermarkThumbShape } from 'fym-shared/src/music24/watermarkUpsell';

/** The facts of a file that the watermark rules read. */
export interface WatermarkFileFacts {
  watermark?: boolean | null;
  /** A preview render (Free, the trial): half size, with the watermark. */
  preview?: boolean | null;
}

/**
 * True when the file carries the Music24 watermark. A kit asset says it (`watermark`); a preview
 * render always has it. A reel answer has `preview` only (and `watermark` when the server sends it).
 */
export function fileHasWatermark(file: WatermarkFileFacts | null | undefined): boolean {
  return file !== null && file !== undefined && (file.watermark === true || file.preview === true);
}

export interface PixelSize {
  width: number;
  height: number;
}

/** The size of the clean file: a preview render is half size, so the clean file is two times it. */
export function cleanSizeOf(width: number | null, height: number | null, preview: boolean): PixelSize | null {
  if (width === null || height === null || width <= 0 || height <= 0) {
    return null;
  }
  return preview ? { width: width * 2, height: height * 2 } : { width, height };
}

/** The shape of a thumbnail tile from a pixel size; a story when the size is not known. */
export function thumbShapeOf(width: number | null, height: number | null): WatermarkThumbShape {
  if (width === null || height === null || width <= 0 || height <= 0) {
    return 'story';
  }
  const ratio = width / height;
  if (ratio > 1.2) {
    return 'wide';
  }
  if (ratio > 0.9) {
    return 'square';
  }
  return ratio > 0.7 ? 'portrait' : 'story';
}

/** The preview size and the full size of each thumbnail shape (`ASSET_SPECS` sizes). */
export const SHAPE_SIZES: Record<WatermarkThumbShape, { preview: PixelSize; full: PixelSize }> = {
  story: { preview: { width: 540, height: 960 }, full: { width: 1080, height: 1920 } },
  square: { preview: { width: 540, height: 540 }, full: { width: 1080, height: 1080 } },
  portrait: { preview: { width: 540, height: 675 }, full: { width: 1080, height: 1350 } },
  wide: { preview: { width: 960, height: 540 }, full: { width: 1920, height: 1080 } },
};
